import React from 'react'
import { Link } from 'react-router-dom'
import { globalState } from '../../../store'
import { Styled } from './styles'
import { usePermissions } from '../../../hooks/usePermissions'

//Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faTimes,
  faConveyorBeltAlt,
  faGiftCard,
  faTruckContainer,
  faMailBulk,
  faBells,
  faSensorAlert,
  faFileInvoice,
  faCalculatorAlt,
  faUsdCircle,
  faCommentAltEdit,
  faFileInvoiceDollar,
  faHistory,
  faLayerGroup,
  faBarcode,
} from '@fortawesome/pro-duotone-svg-icons'
import { faShopify } from '@fortawesome/free-brands-svg-icons'
import { faLoopReturns, faWebhook } from '../../icons'

import { ToolbarItemProps, ToolbarQiuckActionsProps } from './types'

const ToolbarItem = ({ close, url, icon, title }: ToolbarItemProps) => {
  const lineItem = (
    <>
      <div className="item-icon">
        <i>
          <FontAwesomeIcon icon={icon} />
        </i>
      </div>
      <div className="item-title">{title}</div>
    </>
  )
  return url.startsWith('/api') ? (
    <a href={url} className="nav__item">
      {lineItem}
    </a>
  ) : (
    <Link to={url} className="nav__item" onClick={() => close()}>
      {lineItem}
    </Link>
  )
}

/**
 * Quick Actions Panel
 *
 * Permission mapping from design doc (docs/projects/rbac/03-design-doc.md Section 9.5):
 *
 * Warehouse Actions:
 * - Handheld / RF: inbound:operator (warehouse users only)
 * - Warehouse Users: settings:operator
 * - Postage Reconcile: postage:admin
 * - Rate Calculator: postage:viewer
 * - Pricing Overview: billing:operator
 * - Quickbooks Integration: integrations:admin
 * - Async Tasks: settings:admin
 *
 * Customer Actions:
 * - Fulfillment Policies: settings:admin + backorderManagementEnabled
 * - Packaging Rules: products:operator
 * - Promotion Rules: settings:operator
 * - Packing Slip Settings: settings:operator
 * - Shipping Methods: settings:admin
 * - Stock Alerts: inventory:viewer
 * - Email Notifications: settings:operator
 * - Notifications: settings:viewer
 * - Shopify Integration: integrations:admin
 * - Loop Settings: integrations:admin
 * - Webhooks: integrations:operator
 * - Merchant Users: settings:viewer
 */
export const ToolbarQiuckActions = ({ isOpen, close, node }: ToolbarQiuckActionsProps) => {
  const {
    state: { userIsWarehouse, backorderManagementEnabled },
  } = globalState()
  const { hasAccess } = usePermissions()

  // Pre-calculate permissions for cleaner rendering
  // Warehouse Actions
  const canUseRf = hasAccess('inbound', 'operator')
  const canViewWarehouseUsers = hasAccess('settings', 'operator')
  const canReconcilePostage = hasAccess('postage', 'admin')
  const canUseRateCalculator = hasAccess('postage', 'viewer')
  const canViewPricing = hasAccess('billing', 'operator')
  const canManageQuickbooks = hasAccess('integrations', 'admin')
  const canViewAsyncTasks = hasAccess('settings', 'admin')

  // Customer Actions
  const canViewFulfillmentPolicies = userIsWarehouse && backorderManagementEnabled && hasAccess('settings', 'admin')
  const canManagePackagingRules = hasAccess('products', 'operator')
  const canManagePromotionRules = hasAccess('settings', 'operator')
  const canManagePackingSlip = hasAccess('settings', 'operator')
  const canViewShippingMethods = hasAccess('settings', 'viewer')
  const canViewStockAlerts = hasAccess('inventory', 'viewer')
  const canManageEmailNotifications = hasAccess('settings', 'operator')
  const canManageNotifications = hasAccess('settings', 'viewer')
  const canManageShopify = hasAccess('integrations', 'admin')
  const canManageLoop = hasAccess('integrations', 'admin')
  const canManageWebhooks = hasAccess('integrations', 'operator')
  const canViewMerchantUsers = hasAccess('settings', 'viewer')

  // Check if any warehouse actions are visible
  const hasAnyWarehouseActions =
    canUseRf ||
    canViewWarehouseUsers ||
    canReconcilePostage ||
    canUseRateCalculator ||
    canViewPricing ||
    canManageQuickbooks ||
    canViewAsyncTasks

  // Check if any customer actions are visible
  const hasAnyCustomerActions =
    canViewFulfillmentPolicies ||
    canManagePackagingRules ||
    canManagePromotionRules ||
    canManagePackingSlip ||
    canViewShippingMethods ||
    canViewStockAlerts ||
    canManageEmailNotifications ||
    canManageNotifications ||
    canManageShopify ||
    canManageLoop ||
    canManageWebhooks ||
    canViewMerchantUsers

  return (
    <Styled.QuickACtions>
      <div className={'panel ' + (isOpen ? '' : 'hidden')} ref={node}>
        {userIsWarehouse && hasAnyWarehouseActions ? (
          <>
            <div className="panel_head">
              <h3>Warehouse Actions</h3>
              <button onClick={() => close()}>
                <i>
                  <FontAwesomeIcon icon={faTimes} />
                </i>
              </button>
            </div>
            <div className="panel_body">
              {canUseRf && <ToolbarItem {...{ close }} url={'/rf/'} icon={faBarcode} title={'Handheld / RF'} />}
              {canViewWarehouseUsers && (
                <ToolbarItem {...{ close }} url={'/warehouse/users'} icon={faUsers} title={'Warehouse Users'} />
              )}
              {canReconcilePostage && (
                <ToolbarItem {...{ close }} url={'/postage/reconcile/'} icon={faMailBulk} title={'Postage Reconcile'} />
              )}
              {canUseRateCalculator && (
                <ToolbarItem
                  {...{ close }}
                  url={'/postage/rate-calculator/'}
                  icon={faCalculatorAlt}
                  title={'Rate Calculator'}
                />
              )}
              {canViewPricing && (
                <ToolbarItem {...{ close }} url={'/billing/pricing/'} icon={faUsdCircle} title={'Pricing Overview'} />
              )}
              {canManageQuickbooks && (
                <ToolbarItem
                  {...{ close }}
                  url={'/integration/quickbooks/'}
                  icon={faFileInvoiceDollar}
                  title={'Quickbooks Integration'}
                />
              )}
              {canViewAsyncTasks && (
                <ToolbarItem {...{ close }} url={'/core/tasks/'} icon={faHistory} title={'Async Tasks'} />
              )}
            </div>
          </>
        ) : null}
        {hasAnyCustomerActions ? (
          <>
            <div className="panel_head">
              <h3>Customer Actions</h3>
              {!userIsWarehouse || !hasAnyWarehouseActions ? (
                <button onClick={() => close()}>
                  <i>
                    <FontAwesomeIcon icon={faTimes} />
                  </i>
                </button>
              ) : null}
            </div>
            <div className="panel_body">
              {canViewFulfillmentPolicies && (
                <ToolbarItem
                  {...{ close }}
                  url={'/order/fulfillment-policies/'}
                  icon={faLayerGroup}
                  title={'Fulfillment Policies'}
                />
              )}
              {canManagePackagingRules && (
                <ToolbarItem {...{ close }} url={'/package/rule/'} icon={faConveyorBeltAlt} title={'Packaging Rules'} />
              )}
              {canManagePromotionRules && (
                <ToolbarItem {...{ close }} url={'/product/rule/'} icon={faGiftCard} title={'Promotion Rules'} />
              )}
              {canManagePackingSlip && (
                <ToolbarItem
                  {...{ close }}
                  url={'/company/packing-slip/'}
                  icon={faFileInvoice}
                  title={'Packing Slip Settings'}
                />
              )}
              {canViewShippingMethods && (
                <ToolbarItem
                  {...{ close }}
                  url={'/company/shipping-methods/'}
                  icon={faTruckContainer}
                  title={'Shipping Methods'}
                />
              )}
              {canViewStockAlerts && (
                <ToolbarItem
                  {...{ close }}
                  url={'/inventory/stock-alerts'}
                  icon={faSensorAlert}
                  title={'Stock Alerts'}
                />
              )}
              {canManageEmailNotifications && (
                <ToolbarItem
                  {...{ close }}
                  url={'/company/email-notifications/'}
                  icon={faBells}
                  title={'Email Notifications'}
                />
              )}
              {canManageNotifications && (
                <ToolbarItem {...{ close }} url={'/notification/'} icon={faCommentAltEdit} title={'Notifications'} />
              )}
              {canManageShopify && (
                <ToolbarItem {...{ close }} url={'/integration/shop/'} icon={faShopify} title={'Shopify Integration'} />
              )}
              {canManageLoop && (
                <ToolbarItem {...{ close }} url={'/integration/loop/'} icon={faLoopReturns} title={'Loop Settings'} />
              )}
              {canManageWebhooks && (
                <ToolbarItem {...{ close }} url={'/company/webhooks/'} icon={faWebhook} title={'Webhooks'} />
              )}
              {canViewMerchantUsers && (
                <ToolbarItem {...{ close }} url={'/company/users/'} icon={faUsers} title={'Merchant Users'} />
              )}
            </div>
          </>
        ) : null}
      </div>
      <div className={'overlay' + (isOpen ? '' : 'hidden')}></div>
    </Styled.QuickACtions>
  )
}
